/**
 * Applies stable DOM ids to the first top-level block of each section (sections
 * are delimited by `horizontalRule` nodes). Useful when an external sidebar/TOC
 * needs to deep-link to specific blocks.
 *
 * Implemented as a ProseMirror plugin with node Decorations — mutating
 * `element.id` directly on the rendered DOM would be wiped the next time
 * ProseMirror re-renders the node. Decorations are re-applied by PM on every
 * render, which makes them the correct layer for this attribute.
 */

import type { Editor } from '@tiptap/core';
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

type BlockIdsState = { ids: string[] };

export const blockIdsPluginKey = new PluginKey<BlockIdsState>('blockIds');

export const BlockIds = Extension.create({
  name: 'blockIds',
  addProseMirrorPlugins() {
    return [
      new Plugin<BlockIdsState>({
        key: blockIdsPluginKey,
        state: {
          init: () => ({ ids: [] }),
          apply(tr, prev) {
            const next = tr.getMeta(blockIdsPluginKey) as BlockIdsState | undefined;
            return next ?? prev;
          },
        },
        props: {
          decorations(state) {
            const pluginState = blockIdsPluginKey.getState(state);
            const ids = pluginState?.ids ?? [];
            if (ids.length === 0) return null;

            const decorations: Decoration[] = [];
            let sectionIdx = 0;
            let assigned = false;
            state.doc.forEach((node, offset) => {
              if (node.type.name === 'horizontalRule') {
                sectionIdx++;
                assigned = false;
                return;
              }
              if (!assigned && sectionIdx < ids.length) {
                decorations.push(Decoration.node(offset, offset + node.nodeSize, { id: ids[sectionIdx] }));
                assigned = true;
              }
            });
            return DecorationSet.create(state.doc, decorations);
          },
        },
      }),
    ];
  },
});

/**
 * Dispatch a no-op transaction carrying the new id list as plugin metadata.
 * Safe to call on every render — the plugin's `apply` just swaps state when
 * the meta is present; PM will reuse decorations if the doc didn't change.
 */
export const setBlockIds = (editor: Editor, ids: string[]): void => {
  const { view } = editor;
  const tr = view.state.tr.setMeta(blockIdsPluginKey, { ids });
  tr.setMeta('addToHistory', false);
  view.dispatch(tr);
};
